export type UploadQueueState = "idle" | "uploading" | "failed" | "capacity-exceeded" | "disposed"

export interface UploadContext {
  sequence: number
  attempt: number
  signal: AbortSignal
}

export interface UploadQueueOptions<T> {
  upload: (payload: T, context: UploadContext) => Promise<boolean | void>
  startSequence?: number
  concurrency?: number
  timeoutMs?: number
  maxAttempts?: number
  retryBaseMs?: number
  retryMaxMs?: number
  maxBufferedChunks?: number
  maxBufferedBytes?: number
  onStateChange?: (state: UploadQueueState) => void
  onUploaded?: (sequence: number) => void
  onFailed?: (sequence: number) => void
}

export interface EnqueueOptions {
  bytes: number
}

interface QueueEntry<T> {
  sequence: number
  payload: T
  bytes: number
}

export class UploadQueueCapacityError extends Error {
  constructor() {
    super("Speech upload queue capacity exceeded")
    this.name = "UploadQueueCapacityError"
  }
}

/**
 * Bounded, retrying upload queue for captured audio. Payloads stay in memory
 * only until upload succeeds (or the caller disposes the queue), so a failed
 * connection cannot grow the tab without limit.
 */
export class UploadQueue<T> {
  private readonly options: Required<Omit<UploadQueueOptions<T>, "upload" | "onStateChange" | "onUploaded" | "onFailed">> &
    Pick<UploadQueueOptions<T>, "upload" | "onStateChange" | "onUploaded" | "onFailed">
  private sequence: number
  private bufferedBytes = 0
  private pending: QueueEntry<T>[] = []
  private failed = new Map<number, QueueEntry<T>>()
  private activeTasks = new Set<Promise<void>>()
  private controllers = new Map<number, AbortController>()
  private drainWaiters = new Set<() => void>()
  private capacityRejected = false
  private disposed = false
  private currentState: UploadQueueState = "idle"

  constructor(options: UploadQueueOptions<T>) {
    this.options = {
      startSequence: 0,
      concurrency: 2,
      timeoutMs: 30_000,
      maxAttempts: 2,
      retryBaseMs: 250,
      retryMaxMs: 2_000,
      maxBufferedChunks: 96,
      maxBufferedBytes: 64 * 1024 * 1024,
      ...options,
    }
    this.sequence = this.options.startSequence
  }

  get failureCount(): number {
    return this.failed.size + (this.capacityRejected ? 1 : 0)
  }

  get hasFailures(): boolean {
    return this.failureCount > 0
  }

  get capacityExceeded(): boolean {
    return this.capacityRejected
  }

  get state(): UploadQueueState {
    return this.currentState
  }

  enqueue(payload: T, { bytes }: EnqueueOptions): number {
    if (this.disposed) throw new Error("Cannot enqueue on a disposed upload queue")
    if (this.capacityRejected) throw new UploadQueueCapacityError()
    const retainedChunks = this.pending.length + this.failed.size + this.activeTasks.size
    if (
      bytes < 0 ||
      retainedChunks >= this.options.maxBufferedChunks ||
      this.bufferedBytes + bytes > this.options.maxBufferedBytes
    ) {
      this.capacityRejected = true
      this.transition("capacity-exceeded")
      throw new UploadQueueCapacityError()
    }

    const entry = { sequence: this.sequence++, payload, bytes }
    this.bufferedBytes += bytes
    this.pending.push(entry)
    this.pump()
    return entry.sequence
  }

  async drain(): Promise<void> {
    if (this.pending.length === 0 && this.activeTasks.size === 0) return
    await new Promise<void>((resolve) => this.drainWaiters.add(resolve))
  }

  async retryFailed(): Promise<void> {
    if (this.disposed || this.failed.size === 0) return
    const entries = [...this.failed.values()].sort((a, b) => a.sequence - b.sequence)
    this.failed.clear()
    this.pending.push(...entries)
    this.transition("uploading")
    this.pump()
    await this.drain()
  }

  /** Abort in-flight requests and forget all retained audio payloads. */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.controllers.forEach((controller) => controller.abort())
    this.controllers.clear()
    this.pending = []
    this.failed.clear()
    this.bufferedBytes = 0
    this.transition("disposed")
    this.resolveDrainIfSettled()
  }

  private pump() {
    if (this.disposed) return
    while (this.pending.length > 0 && this.activeTasks.size < this.options.concurrency) {
      const entry = this.pending.shift()!
      const task = this.uploadWithRetry(entry)
      this.activeTasks.add(task)
      this.transition("uploading")
      void task.finally(() => {
        this.activeTasks.delete(task)
        this.pump()
        this.resolveDrainIfSettled()
      })
    }
  }

  private async uploadWithRetry(entry: QueueEntry<T>): Promise<void> {
    for (let attempt = 1; attempt <= this.options.maxAttempts; attempt++) {
      if (this.disposed) return
      const controller = new AbortController()
      this.controllers.set(entry.sequence, controller)
      const timeout = window.setTimeout(() => controller.abort(), this.options.timeoutMs)
      let succeeded = false
      try {
        const result = await this.options.upload(entry.payload, {
          sequence: entry.sequence,
          attempt,
          signal: controller.signal,
        })
        succeeded = result !== false
      } catch {
        succeeded = false
      } finally {
        window.clearTimeout(timeout)
        this.controllers.delete(entry.sequence)
      }

      if (this.disposed) return
      if (succeeded) {
        this.bufferedBytes -= entry.bytes
        this.options.onUploaded?.(entry.sequence)
        return
      }
      if (attempt < this.options.maxAttempts) {
        const delay = Math.min(this.options.retryBaseMs * 2 ** (attempt - 1), this.options.retryMaxMs)
        await wait(delay)
      }
    }

    if (!this.disposed) {
      this.failed.set(entry.sequence, entry)
      this.options.onFailed?.(entry.sequence)
      this.transition("failed")
    }
  }

  private resolveDrainIfSettled() {
    if (this.pending.length > 0 || this.activeTasks.size > 0) return
    const waiters = [...this.drainWaiters]
    this.drainWaiters.clear()
    waiters.forEach((resolve) => resolve())
    if (!this.disposed) {
      this.transition(this.capacityRejected ? "capacity-exceeded" : this.hasFailures ? "failed" : "idle")
    }
  }

  private transition(state: UploadQueueState) {
    if (state === this.currentState) return
    this.currentState = state
    this.options.onStateChange?.(state)
  }
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, delayMs))
}
